import { inject, Injectable } from "@angular/core";
import { AnonymousCredential, BlockBlobClient } from "@azure/storage-blob";
import { MediaType } from "src/app/models/mediaitem.model";
import { UploadedMediaItem } from "../_models/event-upload.model";
import { StreappEventsService } from "src/app/services/streapp-events.service";
import { AuthService } from "src/app/services/auth.service";

@Injectable({ providedIn: 'root' })
export class UploadService {

    private streappService = inject(StreappEventsService);

    private authService = inject(AuthService);

    async uploadFiles(
        eventId: string,
        items: UploadedMediaItem[],
        isShop: boolean,
        blur: boolean,
        onUpdate: (index: number, patch: Partial<{ progress: number; finished: boolean }>) => void
    ): Promise<string[]> {

        const tasks = items
            .filter(item => !item.finished)
            .map((item, index) => () =>
                this.uploadSingle(index, item, isShop, blur, eventId, onUpdate)
            );

        return this.runWithLimit(tasks, 2);
    }

    private async runWithLimit<T>(
        tasks: (() => Promise<T>)[],
        limit = 3
    ): Promise<T[]> {

        const results: T[] = [];
        const executing: Promise<any>[] = [];

        for (const task of tasks) {
            const p = task().then(r => {
                executing.splice(executing.indexOf(p), 1);
                return r;
            });

            results.push(await (executing.length >= limit
                ? Promise.race(executing).then(() => p)
                : p));

            executing.push(p);
        }

        return results;
    }

    private async uploadSingle(
        index: number,
        item: UploadedMediaItem,
        isShop: boolean,
        blur: boolean,
        eventId: string,
        onUpdate: (index: number, patch: Partial<{ progress: number; finished: boolean }>) => void
    ): Promise<string> {

        const file = await this.getFile(item);

        const sas = await this.getSas(item, isShop, eventId, blur, file);

        const blobClient = new BlockBlobClient(
            sas.token,
            new AnonymousCredential()
        );

        const totalBytes = file.size;


        await blobClient.uploadData(await file.arrayBuffer(), {
            onProgress: (ev) => {
                const percent = Math.round((ev.loadedBytes / totalBytes) * 100); if (item) { item.progress = percent; }
                onUpdate(index, { progress: percent, finished: false });
            }
        });

        onUpdate(index, { progress: 100, finished: true });


        return sas.mediaItemId;
    }

    private getFile(item: UploadedMediaItem): Promise<File> {
        return new Promise((resolve, reject) => {
            (item.file.fileEntry as FileSystemFileEntry).file(resolve, reject);
        });
    }

    private getSas(
        item: UploadedMediaItem,
        isShop: boolean,
        eventId: string,
        blur: boolean,
        file: File
    ) {
        return this.streappService.getUploadSasUrl(
            this.authService.authorizationHeaderValue,
            file.type.startsWith('image') ? MediaType.Image : MediaType.Video,
            blur,
            eventId,
            file.name.substring(file.name.lastIndexOf('.')).toLowerCase(),
            isShop,
            item.price
        );
    }
}